import type { Props } from 'tippy.js';
import tippy from 'tippy.js';

function fade_action(node: HTMLElement): any {
   node.style.opacity = '0.2';

   const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
         if (entry.isIntersecting) {
            node.classList.add('fade-in');
            node.style.opacity = '1';

            observer.unobserve(node);
         }
      });
   });

   observer.observe(node);

   return () => {
      if (observer) observer.unobserve(node);
   };
}

function scroll_element_into_view(node: HTMLElement, options: ScrollIntoViewOptions | boolean | undefined = { behavior: 'smooth', block: 'start', inline: 'nearest' }) {
   if (options === false) return
   node.scrollIntoView(options === true ? undefined : options);
}

function inview_action(node: HTMLElement, state: { value: boolean }): any {
   const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
         state.value = entry.isIntersecting;
      });
   });

   observer.observe(node);

   return () => {
      if (observer) observer.unobserve(node);
   };
}

function inview_action_callback(node: HTMLElement, callback: (is_inview: boolean) => boolean): any {
   const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
         const is_cancel = callback(entry.isIntersecting)
         if (is_cancel && observer) observer.unobserve(node)
      });
   });

   observer.observe(node);

   return {
      destroy: () => {
         if (observer) observer.unobserve(node);
      }
   }
}

const default_delay: number | [number | null, number | null] = [200, 200];

function create_html_tooltip(text: string) {
   let html_string = `<div class="flex flex-col gap-1">`;

   const lines = text.split('\n');
   for (const line of lines) {
      html_string += `<div class="flex flex-row gap-1">`;
      const words = line.split(' ');
      for (const word of words) {
         html_string += `<div>${word}</div>`;
      }
      html_string += `</div>`;
   }

   html_string += `</div>`;
   return html_string;
}

type TooltipActionProps = Partial<Props> & { cursor?: string | boolean; hide?: boolean, html_content?: string };

function tooltip(node: HTMLElement, props?: TooltipActionProps) {
   function calculate_props(props?: TooltipActionProps) {
      if (props?.hide) return null;
      const html_content = (props?.html_content || props?.content || node.dataset.tooltip) as string;
      if (!html_content) return null;

      const content = props?.html_content ? props?.html_content : create_html_tooltip(html_content);

      const main_props = { allowHTML: true, content, interactive: true, theme: 'material' };

      if (!props) return main_props;

      const { cursor, ...rest_of_props } = props;

      if (cursor)
         node.style.setProperty(
            'cursor',
            typeof cursor === 'string' ? (cursor as string) : 'context-menu'
         );

      return rest_of_props
         ? { ...main_props, ...rest_of_props, content, delay: rest_of_props.delay ?? default_delay }
         : { ...main_props, content, delay: default_delay };
   }

   const tippy_props = calculate_props(props);
   if (!tippy_props) return;
   const tip = tippy(node, tippy_props);

   return {
      update: (new_props: Partial<Props> & { cursor?: string | boolean; hide?: boolean }) => {
         const new_tippy_props = calculate_props(new_props);
         if (!new_tippy_props) return tip.destroy();
         tip.setProps(new_tippy_props);
      },
      destroy: () => {
         tip.destroy();
      }
   };
}

function drag_action<T>(
   node: HTMLElement,
   options?: { payload: T; callback: (dropped_el: Element | null) => 'return_back' | undefined }
) {
   let isMouseDown = false;
   let isDragging = false; // New flag to ensure drag is active
   let initX: number | null = null;
   let initY: number | null = null;

   node.style.userSelect = 'none';

   const originalLeft = node.offsetLeft;
   const originalTop = node.offsetTop;

   function back_to_original() {
      node.style.transition = 'transform 0.3s ease-out';
      node.style.transform = 'none';
      node.style.left = `${originalLeft}px`;
      node.style.top = `${originalTop}px`;

      setTimeout(() => {
         node.style.transition = '';
      }, 300);

      initX = null;
      initY = null;

      document.removeEventListener('mousemove', onmousemove);
      node.removeEventListener('mouseup', onmouseup);
   }

   function onmousedown(e: MouseEvent) {
      console.log('onmousedown', node.id);
      e.preventDefault();
      document.removeEventListener('mousemove', onmousemove);
      node.removeEventListener('mouseup', onmouseup);

      isMouseDown = true;
      isDragging = false; // Drag hasn't started until movement occurs
      node.style.zIndex = '9999';
      document.body.classList.add('no-select');
      if (!initX) initX = e.clientX;
      if (!initY) initY = e.clientY;

      document.addEventListener('mousemove', onmousemove);
      node.addEventListener('mouseup', onmouseup);
   }

   function onmousemove(e: MouseEvent) {
      if (!isMouseDown) return back_to_original();
      isDragging = true; // Drag is active
      const dx = e.clientX - (initX as number);
      const dy = e.clientY - (initY as number);

      node.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
   }

   function onmouseup() {
      console.log('onmouseup', node.id);

      if (!isDragging) return back_to_original(); // Only proceed if it was an active drag

      isMouseDown = false;
      isDragging = false;
      document.body.classList.remove('no-select');

      const draggableRect = node.getBoundingClientRect();
      const droppableElements = Array.from(document.querySelectorAll('.droppable'));

      const intersectingDroppable = droppableElements.find((droppable) => {
         const droppableRect = droppable.getBoundingClientRect();
         return (
            draggableRect.right > droppableRect.left &&
            draggableRect.left < droppableRect.right &&
            draggableRect.bottom > droppableRect.top &&
            draggableRect.top < droppableRect.bottom
         );
      });

      if (!intersectingDroppable || !options?.callback) {
         return back_to_original();
      }

      const return_back = options.callback(intersectingDroppable);
      if (return_back === 'return_back') return back_to_original();

      const dropEvent = new CustomEvent('drop', {
         detail: options?.payload
      });
      intersectingDroppable.dispatchEvent(dropEvent);
      node.style.zIndex = '';

      document.removeEventListener('mousemove', onmousemove);
      node.removeEventListener('mouseup', onmouseup);
   }

   node.addEventListener('mousedown', onmousedown);

   return {
      destroy: () => {
         console.log('ondestroy#########');
         node.removeEventListener('mousedown', onmousedown);
         document.removeEventListener('mousemove', onmousemove);
         node.removeEventListener('mouseup', onmouseup);
      }
   };
}

// function button_confirm_action<T>(node: HTMLButtonElement, { onclick, confirm_title, confirm_text }: { onclick: () => any, confirm_title: string, confirm_text: string }) {

//   const controller = new AbortController();
//   const { signal } = controller

//   node.addEventListener('click', onclick, { signal });

//   return {
//     destroy: () => {
//       console.log('ondestroy#########');
//       controller.abort()
//     },
//   };
// }

export const actions = {
   fade_action,
   inview_action,
   inview_action_callback,
   scroll_element_into_view,
   drag_action,
   tooltip
};

/* DROP ACTION MARKUP USE:


use: actions.drag_action = {{
    payload: { timetable, day_index: day.day_index },
    callback(dropped_el) {
      if (dropped_el?.id === day.day_index.toString()) return 'return_back';
    }
                }}
ondrop = {(e) => {
  const dragged_day_index = (e.detail as any).day_index as number;
  const timetable = (e.detail as any).timetable as NewTimetable;

  console.log('DROPPPPPPPPPPP', { e });
  // if (days[dragged_day_index].timetables.some((tt) => tt.id === timetable.id))
  // 	return console.log('ook same row');
  days[dragged_day_index].timetables = days[dragged_day_index].timetables
    .filter((tt) => tt.id !== timetable.id)
    .sort((a, b) => a.departure_time.localeCompare(b.departure_time));

  days[day.day_index].timetables.push(timetable);
  days[day.day_index].timetables.sort((a, b) =>
    a.departure_time.localeCompare(b.departure_time)
  );
}}


*/
